export interface Permission {
  key: string
  label: string
}

export interface PermissionGroup {
  key: string
  label: string
  permissions: Permission[]
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    permissions: [
      { key: 'dashboard.view', label: 'View dashboard' },
      { key: 'dashboard.view_financials', label: 'View financial summary cards' },
      { key: 'dashboard.view_charts', label: 'View sales charts' },
    ],
  },
  {
    key: 'pos',
    label: 'Point of Sale',
    permissions: [
      { key: 'pos.access', label: 'Access POS' },
      { key: 'pos.view_all', label: 'View all POS sessions' },
      { key: 'pos.view_own', label: 'View own POS sessions only' },
      { key: 'pos.open_register', label: 'Open cash register' },
      { key: 'pos.close_register', label: 'Close cash register' },
      { key: 'pos.view_register', label: 'View register summary' },
      { key: 'pos.add_sale', label: 'Add POS sale' },
      { key: 'pos.edit_sale', label: 'Edit POS sale' },
      { key: 'pos.edit_discount', label: 'Apply discount from POS' },
      { key: 'pos.suspend_sale', label: 'Suspend sale' },
      { key: 'pos.print_receipt', label: 'Print receipt' },
      { key: 'pos.multiple_payment', label: 'Use multiple payment methods' },
      { key: 'pos.end_of_day', label: 'View end of day report' },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    permissions: [
      { key: 'sales.view_all', label: 'View all sales' },
      { key: 'sales.view_own', label: 'View own sales only' },
      { key: 'sales.add', label: 'Add sale' },
      { key: 'sales.edit', label: 'Edit sale' },
      { key: 'sales.apply_discount', label: 'Apply whole-invoice discount' },
      { key: 'sales.return', label: 'Process sale return' },
      { key: 'sales.cancel', label: 'Cancel finalised sale' },
      { key: 'sales.reassign_representative', label: 'Reassign sale representative' },
      { key: 'sales.add_payment', label: 'Add payment to sale' },
      { key: 'sales.export', label: 'Export sales' },
      { key: 'sales.print_invoice', label: 'Print invoice' },
      { key: 'sales.send_statement', label: 'Send customer statement' },
      { key: 'sales.share_invoice', label: 'Generate / copy / revoke public invoice link' },
      { key: 'sales.verify_indication', label: 'Verify / dismiss customer payment indication' },
      { key: 'sales.report.view', label: 'View sales report' },
    ],
  },
  {
    key: 'incentives',
    label: 'Incentives',
    permissions: [
      { key: 'incentives.post_accrual', label: 'Post sales incentive accrual' },
      { key: 'incentives.void_accrual', label: 'Void sales incentive accrual' },
      { key: 'incentives.settle_payable', label: 'Settle sales incentive payable' },
    ],
  },
  {
    key: 'purchases',
    label: 'Purchases',
    permissions: [
      { key: 'purchases.view_all', label: 'View all purchases' },
      { key: 'purchases.view_own', label: 'View own purchases only' },
      { key: 'purchases.add', label: 'Add purchase' },
      { key: 'purchases.add_payment', label: 'Add payment to purchase' },
      { key: 'purchases.receive', label: 'Mark purchase as received' },
      { key: 'purchases.return', label: 'Process purchase return' },
      { key: 'purchases.export', label: 'Export purchases' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    permissions: [
      { key: 'customers.view_all', label: 'View all customers' },
      { key: 'customers.add', label: 'Add customer' },
      { key: 'customers.edit', label: 'Edit customer' },
      { key: 'customers.assign_representative', label: 'Assign customer representative' },
      { key: 'customers.delete', label: 'Delete customer' },
      { key: 'customers.view_balance', label: 'View customer balance' },
      { key: 'customers.write_off', label: 'Write off bad debt' },
      { key: 'customers.add_deposit', label: 'Record customer deposit' },
      { key: 'customers.view_statement', label: 'View customer statement' },
      { key: 'customers.view_ledger', label: 'View customer ledger' },
      { key: 'customers.set_credit_limit', label: 'Set / edit customer credit limit' },
      { key: 'customers.import', label: 'Bulk import customers' },
      { key: 'customers.export', label: 'Export customer lists / ledgers' },
      { key: 'customers.send_reminder', label: 'Send payment reminder (WhatsApp / email)' },
      { key: 'customers.reverse_receipt', label: 'Reverse a customer receipt' },
      { key: 'customers.record_refund', label: 'Record refund to customer' },
      { key: 'customers.cancel_advance', label: 'Cancel a customer advance (move back to bank/cash)' },
    ],
  },
  {
    key: 'suppliers',
    label: 'Suppliers',
    permissions: [
      { key: 'suppliers.view_all', label: 'View all suppliers' },
      { key: 'suppliers.add', label: 'Add supplier' },
      { key: 'suppliers.edit', label: 'Edit supplier' },
      { key: 'suppliers.delete', label: 'Delete supplier' },
      { key: 'suppliers.view_balance', label: 'View supplier balance' },
      { key: 'suppliers.add_prepayment', label: 'Record supplier prepayment' },
      { key: 'suppliers.reverse_payment', label: 'Reverse supplier payment / settlement' },
      { key: 'suppliers.record_refund', label: 'Record refund from supplier' },
      { key: 'suppliers.cancel_advance', label: 'Cancel a supplier advance (move back to bank/cash)' },
      { key: 'suppliers.view_statement', label: 'View supplier statement' },
      { key: 'suppliers.import', label: 'Bulk import suppliers' },
      { key: 'suppliers.export', label: 'Export supplier lists / ledgers' },
    ],
  },
  {
    key: 'products',
    label: 'Products & Inventory',
    permissions: [
      { key: 'products.view', label: 'View products' },
      { key: 'products.add', label: 'Add product' },
      { key: 'products.edit', label: 'Edit product' },
      { key: 'products.delete', label: 'Delete product' },
      { key: 'products.view_cost', label: 'View cost / purchase price' },
      { key: 'products.import', label: 'Import products' },
      { key: 'products.export', label: 'Export products' },
      { key: 'products.print_labels', label: 'Print product labels' },
      { key: 'products.update_price', label: 'Bulk update prices' },
      { key: 'products.manage_bundles', label: 'Create/edit/activate product bundles' },
      { key: 'stock.view', label: 'View stock levels' },
      { key: 'stock.view_all', label: 'View all stock movements' },
      { key: 'stock.view_own', label: 'View own stock movements only' },
      { key: 'stock.adjustment', label: 'Make stock adjustments' },
      { key: 'stock.transfer', label: 'Transfer stock between locations' },
      { key: 'stock.view_expiry', label: 'View expiry report' },
      { key: 'brands.manage', label: 'Manage brands' },
      { key: 'units.manage', label: 'Manage units of measure' },
      { key: 'categories.manage', label: 'Manage product categories' },
    ],
  },
  {
    key: 'expenses',
    label: 'Expenses',
    permissions: [
      { key: 'expenses.view_all', label: 'View all expenses' },
      { key: 'expenses.view_own', label: 'View own expenses only' },
      { key: 'expenses.add', label: 'Add expense' },
      { key: 'expenses.edit', label: 'Edit expense' },
      { key: 'expenses.void', label: 'Void posted expense' },
      { key: 'expenses.view_schedules', label: 'View accrual/prepayment schedules' },
      { key: 'expenses.manage_schedules', label: 'Manage accrual/prepayment schedules' },
      { key: 'expenses.view_recurring', label: 'View recurring expenses' },
      { key: 'expenses.manage_recurring', label: 'Manage recurring expenses' },
      { key: 'expenses.export', label: 'Export expenses' },
      { key: 'expenses.report.view', label: 'View expenses report' },
    ],
  },
  {
    key: 'payroll',
    label: 'Payroll',
    permissions: [
      { key: 'payroll.add_staff', label: 'Add staff member' },
      { key: 'payroll.edit_staff', label: 'Edit staff details' },
      { key: 'payroll.process', label: 'Process salary payment' },
      { key: 'payroll.export', label: 'Export payroll' },
      { key: 'payroll.manage_tax_settings', label: 'Edit PAYE brackets / statutory rates' },
      { key: 'payroll.write_off_loan', label: 'Forgive staff loan' },
      { key: 'payroll.manage_grades', label: 'Manage salary grades' },
      { key: 'payroll.manage_schedules', label: 'Manage working schedules' },
      { key: 'payroll.manage_job_titles', label: 'Manage job titles' },
      { key: 'payroll.manage_loans', label: 'Add and manage staff loans' },
      { key: 'payroll.manage_recurring', label: 'Manage recurring pay items' },
      { key: 'payroll.bulk_import', label: 'Bulk import payroll data' },
      { key: 'payroll.email_payslip', label: 'Email payslip' },
    ],
  },
  {
    key: 'accounting',
    label: 'Accounting',
    permissions: [
      { key: 'accounting.view', label: 'Access accounting module' },
      { key: 'accounting.chart_of_accounts', label: 'View chart of accounts' },
      { key: 'accounting.manage_accounts', label: 'Add and edit accounts' },
      { key: 'accounting.view_journal', label: 'View journal entries' },
      { key: 'accounting.manual_journal', label: 'Post manual journal entries' },
      { key: 'accounting.reverse_journal', label: 'Reverse journal entries' },
      { key: 'accounting.view_ledger', label: 'View account ledger' },
      { key: 'accounting.trial_balance', label: 'View trial balance' },
      { key: 'accounting.opening_balances', label: 'Post opening balances' },
      { key: 'accounting.financial_years', label: 'Manage financial years' },
      { key: 'accounting.close_year', label: 'Close financial year' },
      { key: 'accounting.loans', label: 'View loans' },
      { key: 'accounting.manage_loans', label: 'Add and manage loans' },
      { key: 'accounting.loan_payment', label: 'Make loan payments' },
      { key: 'accounting.assets', label: 'View asset register' },
      { key: 'accounting.manage_assets', label: 'Add and manage assets' },
      { key: 'accounting.depreciation', label: 'Post depreciation' },
      { key: 'accounting.tax_remittance', label: 'Record tax remittances' },
      { key: 'accounting.bank_reconciliation', label: 'Perform bank reconciliation' },
      { key: 'accounting.expense_schedules', label: 'View expense schedules' },
      { key: 'accounting.budget', label: 'View budgets' },
      { key: 'accounting.manage_budget', label: 'Set and update budgets' },
      { key: 'accounting.backfill', label: 'Run accounting backfill' },
      { key: 'accounting.unlock_period', label: 'Unlock locked accounting periods' },
      { key: 'accounting.interbank', label: 'Interbank transfer (posts journal)' },
      { key: 'accounting.dispose_asset', label: 'Dispose / write off fixed asset' },
      { key: 'accounting.owner_drawings', label: 'Record owner drawings' },
      { key: 'accounting.lock_period', label: 'Lock accounting periods' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance & Tax Compliance',
    permissions: [
      { key: 'finance.vat_returns', label: 'File VAT returns' },
      { key: 'finance.wht_returns', label: 'Mark WHT periods remitted' },
      { key: 'finance.cit_returns', label: 'Post CIT accruals' },
      { key: 'finance.payroll_tax_returns', label: 'File PAYE / payroll tax returns' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    permissions: [
      { key: 'reports.sales', label: 'View sales reports' },
      { key: 'reports.purchases', label: 'View purchase reports' },
      { key: 'reports.expenses', label: 'View expense reports' },
      { key: 'reports.profit_loss', label: 'View profit and loss' },
      { key: 'reports.balance_sheet', label: 'View balance sheet' },
      { key: 'reports.trial_balance', label: 'View trial balance' },
      { key: 'reports.aged_debtors', label: 'View aged debtors' },
      { key: 'reports.aged_creditors', label: 'View aged creditors' },
      { key: 'reports.cost_centre', label: 'View cost centre report' },
      { key: 'reports.budget_vs_actual', label: 'View budget vs actual' },
      { key: 'reports.stock', label: 'View stock reports' },
      { key: 'reports.tax', label: 'View tax reports' },
      { key: 'reports.payroll', label: 'View payroll reports' },
      { key: 'reports.end_of_day', label: 'View end of day report' },
      { key: 'reports.export', label: 'Export any report' },
      { key: 'reports.bad_debt', label: 'View bad debt report' },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    permissions: [
      { key: 'settings.business', label: 'Edit business settings' },
      { key: 'settings.business_hours', label: 'Set business hours' },
      { key: 'settings.users', label: 'Manage users' },
      { key: 'settings.users.reset_password', label: 'Reset user password' },
      { key: 'settings.roles', label: 'Manage roles and permissions' },
      { key: 'settings.locations', label: 'Manage locations and warehouses' },
      { key: 'settings.payment_accounts', label: 'Manage payment accounts' },
      { key: 'settings.tax', label: 'Manage tax settings' },
      { key: 'settings.receipt', label: 'Receipt and printer settings' },
      { key: 'settings.currencies', label: 'Manage currencies' },
      { key: 'settings.recurring_expenses', label: 'Manage recurring expenses' },
      { key: 'settings.modules', label: 'Enable and disable modules' },
      { key: 'settings.custom_fields', label: 'Manage custom fields' },
      { key: 'settings.financial_statements', label: 'Edit financial statement settings' },
      { key: 'settings.invoice_numbering', label: 'Edit invoice numbering settings' },
      { key: 'settings.export', label: 'Export data' },
      { key: 'subscription.manage', label: 'Manage subscription & billing' },
    ],
  },
  {
    key: 'system',
    label: 'System & Audit',
    permissions: [
      { key: 'audit.export', label: 'Export audit trail' },
      { key: 'contacts.log_activity', label: 'Log activity / mark follow-up on contacts' },
      { key: 'notifications.manage', label: 'Mark notifications read / unread' },
    ],
  },
]

// ─── All permission keys (for owner role) ─────────────────────────────────────
export const ALL_PERMISSIONS: string[] = PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key))

// ─── Default role permission sets ────────────────────────────────────────────
export const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  owner: ALL_PERMISSIONS,

  // Admin excludes these by default. Owner can grant them explicitly via the
  // role editor — there is no hard code-level block, this is only the default.
  admin: ALL_PERMISSIONS.filter(k =>
    k !== 'accounting.unlock_period' &&
    k !== 'accounting.close_year' &&
    k !== 'accounting.backfill' &&
    k !== 'settings.roles' &&
    k !== 'settings.users.reset_password' &&
    k !== 'payroll.write_off_loan' &&
    k !== 'subscription.manage'
  ),

  manager: [
    'dashboard.view', 'dashboard.view_financials', 'dashboard.view_charts',
    'pos.access', 'pos.open_register', 'pos.close_register', 'pos.view_register',
    'pos.add_sale', 'pos.edit_sale', 'pos.edit_discount',
    'pos.suspend_sale', 'pos.print_receipt', 'pos.multiple_payment', 'pos.end_of_day',
    'sales.view_all', 'sales.add', 'sales.edit', 'sales.return', 'sales.cancel', 'sales.add_payment',
    'sales.reassign_representative', 'sales.apply_discount',
    'sales.print_invoice', 'sales.report.view', 'expenses.report.view',
    'incentives.post_accrual', 'incentives.void_accrual', 'incentives.settle_payable',
    'purchases.view_all', 'purchases.add', 'purchases.add_payment', 'purchases.receive',
    'customers.view_all', 'customers.add', 'customers.edit', 'customers.assign_representative',
    'customers.view_balance', 'customers.view_statement',
    'suppliers.view_all', 'suppliers.add', 'suppliers.edit', 'suppliers.view_balance',
    'products.view', 'products.add', 'products.edit', 'products.view_cost', 'products.manage_bundles',
    'stock.view', 'stock.adjustment', 'stock.transfer',
    'expenses.view_all', 'expenses.add', 'expenses.edit', 'expenses.view_recurring',
    'payroll.manage_schedules', 'payroll.manage_job_titles',
    'reports.sales', 'reports.purchases', 'reports.expenses', 'reports.profit_loss',
    'reports.stock', 'reports.end_of_day',
    // Tier 4 Batch 4:
    'sales.share_invoice', 'sales.verify_indication',
    'customers.set_credit_limit', 'customers.import', 'customers.export', 'customers.send_reminder',
    'suppliers.import', 'suppliers.export',
    'contacts.log_activity', 'notifications.manage',
  ],

  cashier: [
    'dashboard.view',
    'pos.access', 'pos.open_register', 'pos.close_register', 'pos.view_register',
    'pos.add_sale', 'pos.suspend_sale', 'pos.print_receipt', 'pos.end_of_day',
    'sales.view_own', 'sales.add',
    'customers.view_all', 'customers.add',
    'products.view', 'stock.view',
    'reports.end_of_day',
    // Tier 4 Batch 4:
    'sales.share_invoice', 'contacts.log_activity', 'notifications.manage',
  ],

  sales_person: [
    'dashboard.view',
    'pos.access', 'pos.add_sale', 'pos.print_receipt', 'pos.suspend_sale',
    'sales.view_own', 'sales.add', 'sales.edit', 'sales.add_payment', 'sales.apply_discount',
    'customers.view_all', 'customers.add', 'customers.edit', 'customers.view_balance',
    'products.view', 'stock.view',
    // Tier 4 Batch 4:
    'sales.share_invoice', 'contacts.log_activity', 'notifications.manage',
  ],

  accountant: [
    'dashboard.view', 'dashboard.view_financials',
    'accounting.view', 'accounting.chart_of_accounts', 'accounting.manage_accounts',
    'accounting.view_journal', 'accounting.manual_journal', 'accounting.reverse_journal',
    'accounting.view_ledger', 'accounting.trial_balance', 'accounting.opening_balances',
    'accounting.loans', 'accounting.manage_loans', 'accounting.loan_payment',
    'accounting.assets', 'accounting.manage_assets', 'accounting.depreciation',
    'accounting.tax_remittance', 'accounting.bank_reconciliation',
    'accounting.expense_schedules', 'accounting.budget', 'accounting.manage_budget',
    'accounting.interbank', 'accounting.dispose_asset',
    'finance.vat_returns', 'finance.wht_returns', 'finance.cit_returns',
    'finance.payroll_tax_returns', 'customers.write_off',
    'reports.sales', 'reports.purchases', 'reports.expenses', 'reports.profit_loss',
    'reports.balance_sheet', 'reports.trial_balance', 'reports.aged_debtors',
    'reports.aged_creditors', 'reports.cost_centre', 'reports.budget_vs_actual',
    'reports.tax', 'reports.payroll', 'reports.end_of_day', 'reports.export',
    'expenses.view_all', 'expenses.add', 'expenses.void',
    'payroll.manage_grades', 'payroll.manage_loans',
    'payroll.manage_recurring', 'payroll.email_payslip',
    'customers.view_balance', 'customers.view_statement', 'customers.view_ledger',
    'suppliers.view_balance', 'suppliers.view_statement',
    'settings.financial_statements', 'settings.invoice_numbering',
    'accounting.lock_period', 'audit.export',
    // Tier 4 Batch 4:
    'sales.verify_indication', 'customers.set_credit_limit', 'customers.export',
    'customers.send_reminder', 'suppliers.export', 'notifications.manage',
  ],
}

// ─── Page → action-level permission mapping ──────────────────────────────────
// Maps each page (page_key from pageRegistry.ts) to the action-level permission
// keys that live under it. RoleDetailPage uses this to nest action checkboxes
// beneath each edit-level page, and usePermissions.can() uses it for the joint
// enforcement rule (page must be edit-level AND the action must be granted).
// Sensitive actions (see SENSITIVE_ACTIONS) are deliberately excluded.
//
// Page keys verified against pageRegistry.ts; action keys verified against
// PERMISSION_GROUPS above.
export const PAGE_ACTIONS: Record<string, string[]> = {
  // ─── Trading — Sell ────────────────────────────────────────────────────────
  'sell.pos': [
    'pos.edit_discount',
  ],
  'sell.invoices': [
    'sales.edit',
    'sales.apply_discount',
    'sales.return',
    'sales.add_payment',
    'sales.cancel',
    'sales.reassign_representative',
    'sales.print_invoice',
    'sales.share_invoice',
    'sales.verify_indication',
    'sales.report.view',
  ],
  // ─── Trading — Reports ─────────────────────────────────────────────────────
  'trading.incentives': [
    'incentives.post_accrual',
    'incentives.void_accrual',
  ],
  'trading.incentive_payables': [
    'incentives.settle_payable',
  ],
  // ─── Trading — Buy ─────────────────────────────────────────────────────────
  'buy.purchases': [
    'purchases.return',
    'purchases.add',
    'purchases.add_payment',
  ],
  'buy.in_transit': [
    'purchases.receive',
  ],
  // ─── Trading — Expenses ────────────────────────────────────────────────────
  'expenses.list': [
    'expenses.void',
    'expenses.manage_schedules',
    'expenses.manage_recurring',
    'expenses.report.view',
  ],
  // ─── Trading — Inventory ───────────────────────────────────────────────────
  'inventory.stock': [
    'stock.adjustment',
  ],
  'inventory.transfers': [
    'stock.transfer',
  ],
  'inventory.products': [
    'products.update_price',
    'products.add',
    'products.edit',
    'products.delete',
    'products.import',
    'products.export',
    'products.print_labels',
    'products.manage_bundles',
    'categories.manage',
    'brands.manage',
    'units.manage',
  ],
  // ─── Trading — Contacts ────────────────────────────────────────────────────
  'contacts.list': [
    'customers.write_off',
    'customers.add_deposit',
    'customers.reverse_receipt',
    'customers.record_refund',
    'customers.cancel_advance',
    'suppliers.add_prepayment',
    'suppliers.reverse_payment',
    'suppliers.record_refund',
    'suppliers.cancel_advance',
    'customers.add',
    'customers.edit',
    'customers.assign_representative',
    'customers.delete',
    'suppliers.add',
    'suppliers.edit',
    'suppliers.delete',
    'customers.import',
    'customers.export',
    'customers.send_reminder',
    'suppliers.import',
    'suppliers.export',
    'contacts.log_activity',
  ],
  'contacts.credit_limits': [
    'customers.set_credit_limit',
  ],
  // ─── Payroll ───────────────────────────────────────────────────────────────
  'payroll.staff': [
    'payroll.add_staff',
  ],
  'payroll.staff_detail': [
    'payroll.edit_staff',
    'payroll.manage_loans',
    'payroll.write_off_loan',
    'payroll.manage_recurring',
  ],
  'payroll.run': [
    'payroll.process',
  ],
  'payroll.payslips': [
    'payroll.email_payslip',
    'payroll.export',
  ],
  'payroll.settings_grades': [
    'payroll.manage_grades',
  ],
  'payroll.settings_schedules': [
    'payroll.manage_schedules',
  ],
  'payroll.settings_jobs': [
    'payroll.manage_job_titles',
  ],
  'payroll.settings_bulk': [
    'payroll.bulk_import',
  ],
  'payroll.settings_tax': [
    'payroll.manage_tax_settings',
  ],
  // ─── Finance — Accounting ──────────────────────────────────────────────────
  'accounting.journal': [
    'accounting.reverse_journal',
    'accounting.manual_journal',
  ],
  'accounting.chart_of_accounts': [
    'accounting.manage_accounts',
  ],
  'accounting.loans': [
    'accounting.manage_loans',
    'accounting.loan_payment',
  ],
  'accounting.assets': [
    'accounting.manage_assets',
    'accounting.dispose_asset',
    'accounting.depreciation',
  ],
  'accounting.opening_balances': [
    'accounting.opening_balances',
  ],
  'accounting.bank_reconciliation': [
    'accounting.bank_reconciliation',
  ],
  'accounting.interbank': [
    'accounting.interbank',
  ],
  'accounting.owner_drawings': [
    'accounting.owner_drawings',
  ],
  'accounting.tax_remittance': [
    'accounting.tax_remittance',
  ],
  'accounting.budget_setup': [
    'accounting.manage_budget',
  ],
  'accounting.financial_years': [
    'accounting.financial_years',
    'accounting.close_year',
  ],
  'accounting.backfill': [
    'accounting.backfill',
  ],
  // ─── Finance — Tax Compliance ──────────────────────────────────────────────
  'finance.tax_compliance.vat': [
    'finance.vat_returns',
  ],
  'finance.tax_compliance.wht': [
    'finance.wht_returns',
  ],
  'finance.tax_compliance.cit': [
    'finance.cit_returns',
  ],
  'finance.tax_compliance.paye': [
    'finance.payroll_tax_returns',
  ],
  // ─── Finance — Reporting ───────────────────────────────────────────────────
  'reporting.hub': [
    'reports.export',
  ],
  // ─── Business Settings ─────────────────────────────────────────────────────
  'business_settings.profile': [
    'settings.business',
  ],
  'business_settings.invoice_numbering': [
    'settings.invoice_numbering',
  ],
  'business_settings.printer': [
    'settings.receipt',
  ],
  'business_settings.locations': [
    'settings.locations',
  ],
  'business_settings.users': [
    'settings.users',
    'settings.users.reset_password',
  ],
  'business_settings.roles': [
    'settings.roles',
  ],
  'business_settings.tenant_modules': [
    'settings.modules',
  ],
  'business_settings.custom_fields': [
    'settings.custom_fields',
  ],
  'business_settings.finance.period_locks': [
    'accounting.lock_period',
    'accounting.unlock_period',
  ],
  'business_settings.finance.payment_accounts': [
    'settings.payment_accounts',
  ],
  'business_settings.finance.currencies': [
    'settings.currencies',
  ],
  'business_settings.finance.financial_statements': [
    'settings.financial_statements',
  ],
  'business_settings.finance.tax': [
    'settings.tax',
  ],
  // ─── System ────────────────────────────────────────────────────────────────
  'system.audit': [
    'audit.export',
  ],
  'system.notifications': [
    'notifications.manage',
  ],
  'system.subscription': [
    'subscription.manage',
  ],
}

// Action keys currently enforced by can() in production code. Drives the
// "Active" / "Coming Soon" badge in the role editor. Update this as Phases 2-3
// wire up additional actions; once it contains all keys we remove the badge.
export const ENFORCED_ACTIONS = new Set<string>([
  'pos.edit_discount',
  'expenses.void',
  'stock.adjustment',
  'stock.transfer',
  // New in Tier 1 Step 3:
  'sales.add_payment',
  'purchases.add_payment',
  'customers.add_deposit',
  'suppliers.add_prepayment',
  'payroll.process',
  'accounting.manage_loans',
  'accounting.loan_payment',
  'accounting.depreciation',
  'accounting.tax_remittance',
  'expenses.manage_schedules',
  'expenses.manage_recurring',
  // New in Tier 1 Step 4:
  'accounting.unlock_period',
  'accounting.reverse_journal',
  'accounting.close_year',
  // New in Tier 1 (7-leak batch):
  'sales.return',
  'purchases.receive',
  'purchases.return',
  'products.delete',
  'products.update_price',
  'customers.delete',
  'suppliers.delete',
  // New in Tier 2:
  'sales.edit',
  // PR D/D3a — whole-invoice discount:
  'sales.apply_discount',
  'accounting.manage_assets',
  'accounting.manual_journal',
  'accounting.opening_balances',
  // New in Tier 3 (row visibility):
  'stock.view_all',
  'stock.view_own',
  // New in Tier 4 Batch 1A (no-perm-hook money/GL pages):
  'accounting.interbank',
  'accounting.dispose_asset',
  'accounting.owner_drawings',
  'finance.vat_returns',
  'finance.wht_returns',
  'finance.cit_returns',
  'finance.payroll_tax_returns',
  'payroll.manage_tax_settings',
  'payroll.write_off_loan',
  'sales.cancel',
  // New in Tier 4 Batch 2 (inventory metadata gating):
  'products.add',
  'products.edit',
  'products.import',
  'products.export',
  'products.print_labels',
  'products.manage_bundles',
  'categories.manage',
  'brands.manage',
  'units.manage',
  'settings.locations',
  // New in Tier 4 Batch 3a (settings + admin gating):
  'settings.financial_statements',
  'settings.invoice_numbering',
  'settings.users.reset_password',
  'accounting.lock_period',
  'audit.export',
  // New in Tier 4 Batch 3b (payroll HR + accounting child modals + financial years):
  'payroll.manage_grades',
  'payroll.manage_schedules',
  'payroll.manage_job_titles',
  'payroll.manage_loans',
  'payroll.manage_recurring',
  'payroll.bulk_import',
  'payroll.email_payslip',
  // New in Tier 4 Batch 4 (creates, exports, contacts CRUD, shared ExportButton):
  'sales.share_invoice',
  'sales.verify_indication',
  'customers.set_credit_limit',
  'customers.import',
  'customers.export',
  'customers.send_reminder',
  'suppliers.import',
  'suppliers.export',
  'contacts.log_activity',
  'notifications.manage',
  // New in Tier 4 Batch 5 (existing-key sweep — gates already wired in prior
  // batches but never added to this list, so the role editor wrongly showed
  // "Coming Soon"):
  'settings.business',
  'settings.users',
  'settings.roles',
  'settings.tax',
  'settings.receipt',
  'settings.currencies',
  'settings.custom_fields',
  'settings.payment_accounts',
  'settings.modules',
  'purchases.add',
  'customers.add',
  'customers.edit',
  'suppliers.add',
  'suppliers.edit',
  'customers.write_off',
  'sales.print_invoice',
  'reports.export',
  'payroll.add_staff',
  'payroll.edit_staff',
  'payroll.export',
  'accounting.manage_accounts',
  'accounting.financial_years',
  'accounting.manage_budget',
  'accounting.bank_reconciliation',
  'accounting.backfill',
  // Batch 5 — row-visibility pairs wired via viewScope() (stock pair was
  // already listed in Tier 3; these consumers exist but were missed):
  'sales.view_all',
  'sales.view_own',
  'purchases.view_all',
  'purchases.view_own',
  'expenses.view_all',
  'expenses.view_own',
  // Batch 5 — new POS visibility keys (Special Focus #1: viewScope('pos')
  // consumer exists in POSSessionsPage; Tier 3 promised the keys):
  'pos.view_all',
  'pos.view_own',
  // Batch 5 — new subscription key (Special Focus #3: owner-only billing):
  'subscription.manage',
  // Build 1 — reverse-settlement (supplier payment reversal):
  'suppliers.reverse_payment',
  // Build 5 — forward supplier refund (cash returned against an advance):
  'suppliers.record_refund',
  // Build 4 — reverse-settlement (customer receipt reversal):
  'customers.reverse_receipt',
  // Build 6 — forward customer refund (cash paid back against a credit):
  'customers.record_refund',
  // Build 7 — cancel advance (moves an advance back to bank/cash):
  'suppliers.cancel_advance',
  'customers.cancel_advance',
  // PR A1 — sales attribution (assign a staff representative to a customer):
  'customers.assign_representative',
  // PR A2.4 — per-sale representative override (Manager+):
  'sales.reassign_representative',
  // PR B / B2 — sales-rep incentive accrual posting (Manager+):
  'incentives.post_accrual',
  // PR B / B3 — sales-rep incentive accrual void (Manager+):
  'incentives.void_accrual',
  // PR B / B4 — sales-rep incentive payable settlement (Manager+):
  'incentives.settle_payable',
])

/**
 * Sensitive actions — these are destructive or irreversible. They can still be
 * delegated by the owner to any role, but the role editor surfaces a warning
 * when granting them.
 *
 * Owner / super_admin always bypass via can() top-level check. There is no hard
 * code-level block — only owner decides who gets these via the role editor.
 */
export const SENSITIVE_ACTIONS: readonly string[] = [
  'accounting.unlock_period',
  'accounting.close_year',
  'accounting.backfill',
  'payroll.write_off_loan',
  // New in Tier 4 Batch 3a:
  'settings.users.reset_password',
  'audit.export',
  'settings.roles',
  // New in Tier 4 Batch 5:
  'subscription.manage',
  // Build 1 — destructive financial action (reverses a posted settlement):
  'suppliers.reverse_payment',
  // Build 5 — moves money (records cash refunded by a supplier):
  'suppliers.record_refund',
  // Build 4 — destructive financial action (reverses a posted customer receipt):
  'customers.reverse_receipt',
  // Build 6 — moves money (records cash refunded to a customer):
  'customers.record_refund',
  // Build 7 — moves money (cancels an advance back to bank/cash):
  'suppliers.cancel_advance',
  'customers.cancel_advance',
] as const

// ─── Row-visibility groups (All / Own / None) ────────────────────────────────
// Drives the visibility selector in the role editor. Each group maps a module's
// `view_all` / `view_own` permission pair (already in PERMISSION_GROUPS) to the
// registry module + section the selector renders under, and to the DB owner
// column the "own" filter keys off at runtime (usePermissions().viewScope).
//
// Only groups that are wired BOTH ways are listed: the keys exist here AND a
// page calls viewScope(group) at runtime. POS is now wired (Batch 5 added the
// keys; POSSessionsPage calls viewScope('pos')). Payroll visibility keys were
// dropped in Tier 4 Batch 6 — re-add the key + viewScope consumer together if
// payroll row-scoping is needed later.
export interface VisibilityGroup {
  group: string       // permission prefix, e.g. 'sales'
  noun: string        // shown as "All <noun> in the business"
  module: string      // registry module the selector renders under
  section: string     // registry section the selector renders under
  viewAll: string     // '<group>.view_all'
  viewOwn: string     // '<group>.view_own'
  ownerColumn: string // DB column the "own" scope filters on
}

export const VISIBILITY_GROUPS: readonly VisibilityGroup[] = [
  { group: 'sales',     noun: 'sales',          module: 'Trading', section: 'Sell',      viewAll: 'sales.view_all',     viewOwn: 'sales.view_own',     ownerColumn: 'created_by' },
  { group: 'purchases', noun: 'purchases',      module: 'Trading', section: 'Buy',       viewAll: 'purchases.view_all', viewOwn: 'purchases.view_own', ownerColumn: 'created_by' },
  { group: 'expenses',  noun: 'expenses',       module: 'Trading', section: 'Expenses',  viewAll: 'expenses.view_all',  viewOwn: 'expenses.view_own',  ownerColumn: 'created_by' },
  { group: 'stock',     noun: 'stock movements', module: 'Trading', section: 'Inventory', viewAll: 'stock.view_all',     viewOwn: 'stock.view_own',     ownerColumn: 'created_by' },
  { group: 'pos',       noun: 'POS sessions',   module: 'Trading', section: 'Sell',      viewAll: 'pos.view_all',       viewOwn: 'pos.view_own',       ownerColumn: 'user_id' },
] as const

// Every view_all / view_own key the visibility selector manages. The role editor
// persists exactly these into role_permissions, leaving all other rows alone.
export const VISIBILITY_KEYS: readonly string[] = VISIBILITY_GROUPS.flatMap(g => [g.viewAll, g.viewOwn])

// Reverse lookup: which page does this action live under? Returns undefined for
// cross-cutting / system actions that are not page-scoped (those are never
// joint-gated by page level).
const ACTION_TO_PAGE: Record<string, string> = Object.entries(PAGE_ACTIONS).reduce(
  (acc, [pageKey, actions]) => {
    for (const action of actions) acc[action] = pageKey
    return acc
  },
  {} as Record<string, string>,
)

export function findPageForAction(actionKey: string): string | undefined {
  return ACTION_TO_PAGE[actionKey]
}
